import { DateRange } from 'rewire-common';
import deepequals from 'fast-deep-equal';
import { ITimecardTest, IResult, ISubscriptionRecord, ProductName, IEmployee } from '../DataTypes';
import DataQueries, {
  // TimecardTestFilterInput,
} from '../graphql/Queries';
import DataMutations from '../graphql/Mutations';
import { timecardTestRouteManager, IRouteManagerState } from '../main-navigation/RouteManager';
import timecardTestsSubscriptionService, {
  TimecardTestsSubscriptionModel,
  defaultTimecardTestSchema,
} from '../services/TimecardTestsSubscriptionService';
import { isSame } from '../utility/DateUtils';
import RoutedEntityListModel from '../ui/RoutedEntityListModel';
import { notifications } from '../ui/Notifications';
import timecardTestModel from './TimecardTestModel';
import { SortDirection } from '@material-ui/core/TableCell';

export class TimecardTestListModel extends RoutedEntityListModel {
  timecardTestsSubscriptionModel: TimecardTestsSubscriptionModel;

  constructor() {
    super();

    this.timecardTestsSubscriptionModel = timecardTestsSubscriptionService.createSubscriptionModel({
      returnSchema: defaultTimecardTestSchema,
      filter: {},
      pagingModel: this.pagingModel,
      externalEntitiesContainer: this.tests,
      subscriptionCallback: this.subscriptionCallback,
    });
  }

  reset() {
    super.reset();
    this.timecardTestsSubscriptionModel.reset();
  }

  navigateTo(testId?: string) {
    timecardTestRouteManager.navigateTo({ selectedTimecardTestId: testId });
  }

  protected getTitle(t?: ITimecardTest): string {
    return t
      ? `Timecard Test ${timecardTestRouteManager.testView} - ${t.name} - ${ProductName}`
      : `Timecard Test ${timecardTestRouteManager.testView} - ${ProductName}`;
  }

  getTest(testId: string): ITimecardTest | undefined {
    return this.getEntity(testId) as ITimecardTest | undefined;
  }

  setTestsSubscriptionFilter() {
    this.timecardTestsSubscriptionModel.filter = {
      ...timecardTestRouteManager.filters,
    };
  }

  setTestsSubscriptionSort() {
    this.timecardTestsSubscriptionModel.sort = this.getTimecardTestSortFn(timecardTestRouteManager.sorts);
  }

  get tests(): ITimecardTest[] {
    return this.state.entities as ITimecardTest[];
  }

  get testCount(): number {
    return this.tests.length;
  }

  get selectedTest(): ITimecardTest | undefined {
    return this.state.selectedEntity as ITimecardTest | undefined;
  }

  set selectedTest(test: ITimecardTest | undefined) {
    this.state.selectedEntity = test;
  }

  get canRunSelectedTest(): boolean {
    return !!this.selectedTest && timecardTestModel.canRunTests();
  }

  get canRunAllTests(): boolean {
    return this.testCount > 0 && timecardTestModel.canRunTests();
  }

  protected shouldLoad(args?: {
    prevState: IRouteManagerState;
    currState: IRouteManagerState;
    effective?: DateRange;
    route?: string;
  }): boolean {
    if (!args) return true;
    if (!isSame(args.effective, args.prevState.effective)) return true;
    if (args.prevState.route !== args.route) return true;
    if (!deepequals(args.prevState.filters, args.currState.filters)) return true;
    if (!deepequals(args.prevState.sorts, args.currState.sorts)) return true;
    return false;
  }

  load = async (selectedTestId?: string, otherArgs?: Record<string, unknown>) => {
    if (this.shouldLoad(otherArgs as any)) {
      this.setTestsSubscriptionFilter();
      this.setTestsSubscriptionSort();
      await this.pagingModel.load();
    }

    let selectedTimecardTest: ITimecardTest | undefined;
    if (selectedTestId) {
      selectedTimecardTest = this.getTest(selectedTestId);
      if (!selectedTimecardTest) {
        selectedTimecardTest = await this.loadItem(selectedTestId);
      }
    }
    this.select(selectedTimecardTest);
  };

  async _pagingLoad(): Promise<{ data?: ITimecardTest[]; cursor?: string; totalCount: number }> {
    const { filters, sorts } = timecardTestRouteManager;

    const results: IResult<ITimecardTest> = (await DataQueries.getTimecardTests(
      defaultTimecardTestSchema,
      filters || {},
      sorts,
      this.pagingModel.itemsPerPage,
      this.pagingModel.currentPage,
    )) as IResult<ITimecardTest>;

    setTimeout(() => this.timecardTestsSubscriptionModel.refreshUpdate(), 0);
    return results;
  }

  getTimecardTestSortFn(sorts: any) {
    let key: keyof IEmployee | undefined = undefined;
    let direction: SortDirection | undefined = undefined;
    let sortFn = undefined;
    if (sorts?.employee) {
      key = 'name';
      direction = sorts.employee.direction;
    } else if (sorts?.code) {
      key = 'code';
      direction = sorts.code.direction;
    }

    if (key && direction) {
      const compareFn =
        direction === 'asc'
          ? (t1: ITimecardTest, t2: ITimecardTest) => t1.employee?.[key!]!.localeCompare(t2?.employee[key!]!)
          : (t1: ITimecardTest, t2: ITimecardTest) => t2?.employee[key!]!.localeCompare(t1?.employee[key!]!);
      sortFn = (t1: ITimecardTest, t2: ITimecardTest) => {
        return compareFn(t1, t2);
      };
    }

    return sortFn;
  }

  async loadItem(testId: string): Promise<ITimecardTest | undefined> {
    const results = (await DataQueries.getTimecardTestsByIds(defaultTimecardTestSchema, [testId])) as ITimecardTest[] | undefined;
    return (results?.length && results[0]) || undefined;
  }

  makeLink(testId: string): string {
    return timecardTestRouteManager.createPayPlanLinkFor({ selectedTimecardTestId: testId });
  }

  subscriptionCallback = (sr: ISubscriptionRecord<ITimecardTest>) => {
    // reselect selected test if there is one, and reload it if it was changed
    const e = this.getEntity(timecardTestRouteManager.selectedTimecardTestId);
    this.select(e);
    if (this.selectedTest && (sr as any)['test'].id === this.selectedTest.id) {
      timecardTestModel.load(this.selectedTest.id);
    }
  };

  async runSelectedTest(): Promise<boolean> {
    if (!this.canRunSelectedTest) {
      return false;
    }

    const filters = { ids: [this.selectedTest!.id] };
    const result = await DataMutations.runTests(filters);
    if (result) {
      notifications.success('Test Ran Successfully');
    } else {
      notifications.error('Test Failed To Run');
    }
    return result;
  }

  async runAllTests(): Promise<boolean> {
    if (!this.canRunAllTests) {
      return false;
    }

    const filters = timecardTestRouteManager.filters;
    const result = await DataMutations.runTests(filters);
    if (result) {
      notifications.success('All Tests Ran Successfully');
    } else {
      notifications.error('Tests Failed To Run');
    }
    return result;
  }
}

const timecardTestListModel = new TimecardTestListModel();
export default timecardTestListModel;
